<template lang="pug">
.flex.items-center.mb-8
  h1.mr-4 {{ valueList?.name ?? $t("inputValues") }}
  el-popover(ref="customIdPopover", trigger="click", width="300px")
    template(#reference)
      el-tag(v-if="showEditCustomId", type="info") 
        .flex.items-center
          span.mr-2 {{ valueList?.customId ?? "" }}
          el-icon
            Edit
    .popover-content
      .smx-item.mb-4
        .smx-label {{ $t("customId") }}
        el-input(v-model="customId")
      .flex.justify-end.w-full
        el-button(
          v-loading="customIdLoading",
          type="primary",
          @click="saveCustomId"
        ) {{ $t("save") }}

.flex.items-center.mb-8
  .flex.items-center.mr-8(v-if="hasFilter")
    label.mr-8 {{ filterMetaData?.name ?? $t("filter") }}
    el-select.min-w-48(
      v-model="filterValue",
      v-loading="filterValueListLoadState.loading"
    )
      el-option(
        v-for="option in filterValues",
        :key="option.uuid",
        :value="option.uuid",
        :label="option.name"
      ) 
  label.mr-8 {{ $t("name") }}
  .w-96.mr-8
    el-input.mr-8(v-model="name", @keyup.enter="addInputValue")
  el-button(icon="Plus", type="primary", @click="addInputValue") {{ $t("add") }}
.flex.flex-wrap.mb-2(v-if="!hasFilter && valueListItems.length > 0")
  el-tag.mr-4(
    v-for="item in valueListItems",
    :key="item.name",
    closable,
    @close="removeInputValue(item)"
  ) {{ item.name }}
.filtered-tags(v-else-if="hasFilter && valueListItems.length > 0")
  .flex.flex-wrap.mb-2
    el-tag.mr-4.mb-8(
      v-for="item in valueListItemsByFilter[null]",
      :key="item.name",
      closable,
      @close="removeInputValue(item)"
    ) {{ item.name }}
  template(v-for="filterItem in filterValues", :key="filterItem.uuid")
    .flex.items-center
      label.mr-8.mb-4 {{ filterItem.name }}
      .flex.flex-wrap
        el-tag.mr-4.mb-4(
          v-for="item in valueListItemsByFilter[filterItem.uuid]",
          :key="item.name",
          closable,
          @close="removeInputValue(item)"
        ) {{ item.name }}
    hr.mb-4
.placeholder.text-slate-400.font-bold(v-else) {{ $t("noValues") }}

.filter-list.mt-8(v-if="isUsedAsFilter")
  label {{ $t("usedAsFilterIn") }}
  .flex.flex-col.mt-4
    router-link.mb-4(
      v-for="vl in filterForValueLists",
      :key="vl.uuid",
      :to="{ name: 'assetAdminInputValuesDetail', params: { id: vl.uuid } }"
    ) 
      a.link {{ vl.name }}

el-dialog(
  :model-value="markForDeletion !== null",
  @model-value="() => (markForDeletion = null)"
)
  p {{ $t("confirmDeleteValueList", { valueList: markForDeletion?.name }) }}
  template(#footer)
    .flex.justify-end
      el-button(@click="markForDeletion = null") {{ $t("cancel") }}
      el-button(type="danger", @click="removeMarkedItem") {{ $t("delete") }}
</template>

<script lang="ts" setup>
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useTwinStore } from "@shared/stores/twinManagement";
import { useMenuStore } from "@shared/stores/menu";
import {
  type LoadState,
  stateError,
  stateInitial,
  stateLoaded,
  stateLoading,
} from "@shared/models/state";
import {
  type ValueListItem,
  getValueListItems,
  type ValueListMetaData,
} from "@shared/services/twinData";
import { ElNotification } from "element-plus";
import { useI18n } from "vue-i18n";
import { useAssetManagementAdmin } from "@shared/hooks/assetManagementAdmin";

const assetManagementAdmin = useAssetManagementAdmin();

const twinStore = useTwinStore();
const { t } = useI18n();

const valueListItemsLoadingState = computed(() =>
  twinStore.valueListItemsLoadingState.get(props.id),
);

const loading = computed(() => valueListItemsLoadingState.value?.loading);
const valueListItems = computed(
  () => valueListItemsLoadingState.value?.result ?? [],
);

const props = defineProps<{ id: string; showEditCustomId: boolean }>();

onMounted(() => {
  loadData();
});

function loadData() {
  twinStore.loadValueLists({ refresh: false });
  twinStore.loadValueListItems(props.id, false);
  loadFilterValueList();
}

const isCreateEnabled = computed(() => {
  let isEnabled = name.value !== "";
  isEnabled =
    isEnabled &&
    !valueListItems.value?.some(
      (item) =>
        item.name === name.value && item.filterValue === filterValue.value,
    );
  if (hasFilter.value) {
    isEnabled = isEnabled && filterValue.value !== "";
  }
  // add fiulter check
  return isEnabled;
});

function getCreateUnavailableMessage() {
  if (name.value === "") {
    return t("pleaseEnterItem", { item: t("name") });
  }
  if (hasFilter.value && filterValue.value === "") {
    return t("pleaseEnterItem", {
      item: filterMetaData.value?.name ?? t("filter"),
    });
  }
  if (
    valueListItems.value?.some(
      (item) =>
        item.name === name.value && item.filterValue === filterValue.value,
    )
  ) {
    return t("itemAlreadyExists");
  }
  return "";
}

const name = ref("");

function addInputValue() {
  if (!isCreateEnabled.value) {
    ElNotification({
      message: getCreateUnavailableMessage(),
      type: "info",
    });
    return;
  }
  twinStore.createValueListItem({
    name: name.value,
    value: name.value,
    valueListId: props.id,
    filterValue: filterValue.value !== "" ? filterValue.value : null,
  });
  name.value = "";
}

const markForDeletion = ref<ValueListItem | null>(null);

function removeMarkedItem() {
  twinStore.removeValueListItem(props.id, markForDeletion.value.uuid);
  markForDeletion.value = null;
}

function removeInputValue(item: ValueListItem) {
  // NOTE: if the value might be a filter, we issue a warning to the user with information that the delete will cascade
  if (isUsedAsFilter.value) {
    markForDeletion.value = item;
  } else {
    twinStore.removeValueListItem(props.id, item.uuid);
  }
}

const valueListItemsByFilter = computed(() => {
  return valueListItems.value.reduce((agg, curr) => {
    if (agg[curr.filterValue] === undefined) {
      agg[curr.filterValue] = [];
    }
    agg[curr.filterValue].push(curr);
    return agg;
  }, {});
});

const valueList = computed(() => {
  return twinStore.valueListsLoadingState.result.find(
    (item) => item.uuid === props.id,
  );
});

// Filter
const filterId = computed(() => {
  return valueList.value?.filterId;
});

const hasFilter = computed(() => {
  return filterId.value ?? false;
});

watch(filterId, loadFilterValueList);

const filterValueListLoadState = ref<LoadState<ValueListItem[]>>({
  ...stateInitial,
  result: [],
});

const filterValues = computed(() => filterValueListLoadState.value.result);

const filterValue = ref("");

const filterMetaData = computed(() =>
  (twinStore.valueListsLoadingState?.result ?? []).find(
    (item) => item.uuid === filterId.value,
  ),
);

async function loadFilterValueList() {
  if (filterId.value) {
    filterValueListLoadState.value = { ...stateLoading, result: [] };
    const response = await getValueListItems(filterId.value).catch((e) => {
      filterValueListLoadState.value = { ...stateError, result: [] };
      throw e;
    });
    filterValueListLoadState.value = {
      ...stateLoaded,
      result: response.data.result,
    };
  }
}

const filterForValueLists = computed(() => {
  return (twinStore.valueListsLoadingState?.result ?? []).filter(
    (item) => item.filterId === props.id,
  );
});

const isUsedAsFilter = computed(() => {
  return filterForValueLists.value.length > 0;
});

// custom id
function initMetaData() {
  if (valueList.value?.customId) {
    customId.value = valueList.value.customId;
  }
}

onMounted(() => {
  initMetaData();
});

watch(valueList, initMetaData);

const customId = ref(valueList.value?.customId ?? "");

const customIdLoading = ref(false);

function saveCustomId() {
  customIdLoading.value = true;
  twinStore
    .updateValueList(props.id, { customId: customId.value })
    .then(() => {
      closeCustomIdDialog();
    })
    .finally(() => {
      customIdLoading.value = false;
    });
}

const customIdPopover = ref(null);

function closeCustomIdDialog() {
  customIdPopover.value?.hide();
  customId.value = "";
}
</script>
